import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getStockMovement } from "../services/StockMovementService";
import type { StockMovement } from "../types/stock-movement.types";
import StockMovementOverviewContext from "./StockMovementOverviewContext";
import Section from "./Section";

const StockMovementOverview = () => {
  document.title = getDocumentTitle("Stock Movement Details");
  const { id } = useParams();
  const [stockMovement, setStockMovement] = useState<StockMovement | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!id) return;
    const res = await getStockMovement(Number(id));
    if (res.success && res.data) setStockMovement(res.data);
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getStockMovement(Number(id));
        if (!cancelled && res.success && res.data) {
          setStockMovement(res.data);
        } else if (!cancelled) {
          setStockMovement(null);
        }
      } catch {
        if (!cancelled) setStockMovement(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <PageLoadingSpinner message="Loading stock movement…" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Stock movement details"
          pageTitle="Inventory Management"
          items={[
            {
              label: "Stock movements",
              path: "/inventory-management/stock-movements",
            },
          ]}
        />
        <StockMovementOverviewContext.Provider
          value={{ stockMovement, refetch }}
        >
          <Section />
        </StockMovementOverviewContext.Provider>
      </Container>
    </div>
  );
};

export default StockMovementOverview;
